import React, { useEffect } from "react";
import { useArrayContext } from "../../../state/ArrayContext";
import { useSiteContext } from "../../../state/SiteContext";
import { MetadataRequest } from "../../../shared/models/metadata-request.model";
import { useFilterState } from "../../../state/FilterContext";
import { Metadata } from "../../../shared/models/metadata.model";
import { services } from "../../../globals";
import { mapMetadataToItem } from "../../../shared/utils/source-item.utils";
import { useBookContext } from "../../../state/BookContext";
import { encodeBookEditionKey, normalizeSitePath, pickExplicitValidityStep } from "../../../shared/utils/book-edition-key.utils";

interface SearchQueryProps {
    contentType?: string;
    /** When true, base file paths (Site.files) are included in search results. Default false. */
    includeBasefiles?: boolean | string;
    /** When true, restrict results to files referenced from the resolved book edition. */
    restrictToBook?: boolean | string;
}

function coerceTrueFlag(value: boolean | string | undefined): boolean {
    return value === true || value === "true";
}

function bookMetadataMatchesPath(bookMetadata: Metadata | undefined, bookPath: string): boolean {
    const metaPath = normalizeSitePath(bookMetadata?.path) ?? bookMetadata?.path;
    const resolvedPath = normalizeSitePath(bookPath) ?? bookPath;
    return Boolean(metaPath && resolvedPath && metaPath === resolvedPath);
}

/***
 * Array source that returns the result of a search query.
 */
export const SearchQuery = React.memo(function SearchQuery({
    contentType,
    includeBasefiles = false,
    restrictToBook = false,
}: SearchQueryProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const { siteState } = useSiteContext();
    const filterState = useFilterState();
    const { bookState } = useBookContext();
    const restrict = coerceTrueFlag(restrictToBook);
    const includeBase = coerceTrueFlag(includeBasefiles);
    const bookPath = bookState.bookPath;
    const bookMetadata = bookState.bookMetadata;
    const bookLookupSettled = bookState.bookLookupSettled !== false;

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            if (!siteState.site?.name) {
                return;
            }
            if (restrict && bookPath && !bookLookupSettled) {
                setArray([]);
                return;
            }
            const request: MetadataRequest = { ...filterState };
            contentType && (request.contentType = contentType);
            if (includeBase) request.includeBasefiles = true;
            if (restrict && bookPath && bookLookupSettled && bookMetadataMatchesPath(bookMetadata, bookPath) && bookMetadata) {
                const step = pickExplicitValidityStep(bookMetadata, filterState.validity);
                if (step !== null) {
                    request.restrictToBook = true;
                    request.bookEditionKey = encodeBookEditionKey(bookMetadata.path, step, bookMetadata.lang);
                }
            }
            const searchlist: Array<Metadata> = await services.search.search(
                siteState.site.name,
                request,
            );
            const result = searchlist.map((hit, ix) => {
                return { ...mapMetadataToItem(hit), position: ix + 1 };
            });
            if (isMounted) {
                setArray(result);
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, [
        siteState,
        filterState,
        contentType,
        includeBase,
        restrict,
        bookPath,
        bookMetadata,
        bookLookupSettled,
        setArray,
    ]);

    return <></>;
});
